import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const baseUrl = () => (import.meta.env.BASE_URL || '').replace(/\/$/, '') || '';
const VIDEO_OPERATION_SLIDE_IMAGES = [
  'images/event-hero.png',
  'images/video-operation/hero.png',
  'images/video-operation/vo1.png',
  'images/video-operation/vo2.png',
  'images/video-operation/vo3.png',
  'images/video-operation/vo4.png',
  'images/video-operation/vo5.png'
];

export default function Services() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const [videoOpSlideIndex, setVideoOpSlideIndex] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // 映像オペエリアのスライド画像をローテーション
  useEffect(() => {
    if (!isVisible) return;
    const timer = setInterval(() => {
      setVideoOpSlideIndex((prev) => (prev + 1) % VIDEO_OPERATION_SLIDE_IMAGES.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [isVisible]);

  type EquipmentCategory = { category: string; items: string[] };
  const [equipmentModal, setEquipmentModal] = useState<{ title: string; list: EquipmentCategory[] } | null>(null);

  const services: Array<{
    number: string;
    title: string;
    subtitle: string;
    description: string;
    features: string[];
    icon: string;
    color: string;
    equipmentList?: EquipmentCategory[];
    fullWidth?: boolean;
    slideImages?: string[];
  }> = [
    {
      number: '01',
      title: 'Webサイト制作',
      subtitle: 'Web Design & Development',
      description: `企画からデザイン、運用まで。
目的に沿って“使われ続ける”Webサイトを制作します。`,
      features: ['レスポンシブデザイン', 'システム連携', 'CMS構築', 'サイト保守'],
      icon: 'ri-window-line',
      color: 'teal'
    },
    {
      number: '02',
      title: 'システム・Webアプリ開発',
      subtitle: 'System & Web Application',
      description: 'Webサイトと連携したシステムやWebアプリケーションを包括的に構築します。',
      features: ['業務システム', 'Webアプリ', 'API連携', 'データベース設計'],
      icon: 'ri-code-box-line',
      color: 'orange'
    },
    {
      number: '03',
      title: 'デジタルサイネージ',
      subtitle: 'Digital Signage Solutions',
      description: '効果的なデジタルサイネージシステムの構築・運用をサポートします。',
      features: ['コンテンツ制作', 'システム構築', '運用サポート', 'メンテナンス'],
      icon: 'ri-tv-line',
      color: 'green'
    },
    {
      number: '04',
      title: 'グラフィックデザイン',
      subtitle: 'Graphic Design Services',
      description: 'ブランディングからマーケティング素材まで幅広いデザインを提供します。',
      features: ['ロゴデザイン', 'パンフレット制作', 'バナー制作', 'ブランディング'],
      icon: 'ri-palette-line',
      color: 'pink'
    },
    {
      number: '05',
      title: 'イベント運営企画・映像オペレーション',
      subtitle: '学会運営（医療系中心）／Event Planning & Video Operation',
      description: '学会運営を中核に、医療系をはじめとする各種学会の運営業務をワンストップで請け負います。企画・当日運営・オンライン／ハイブリッド配信・映像・音響まで一貫対応。ウェビナー、Zoom・YouTube配信、配信機材レンタルも行い、入学式・卒業式、講演会・音楽ライブなど幅広くサポートします。',
      features: ['学会運営（医療系を中心とした各種学会）', 'ライブ配信・ハイブリッド配信', 'マルチカメラ・リモート中継', 'イベント技術運用・音響'],
      icon: 'ri-video-line',
      color: 'violet',
      fullWidth: true,
      slideImages: VIDEO_OPERATION_SLIDE_IMAGES,
      equipmentList: [
        { category: 'ビデオミキサー（スイッチャー）', items: ['ROLAND V-80HD'] },
        { category: 'カメラ', items: ['Panasonic LUMIX S5M2X', 'Panasonic LUMIX GH5M2', 'Canon EOS 80D', 'GoPro9'] },
        { category: 'レンズ（Lマウント）', items: ['SIGMA Art 24-70mm F2.8 DG DN', 'SIGMA Sports 150-600mm F5-6.3 DG DN OS', 'Panasonic LUMIX S 20-60mm F3.5-5.6', 'Panasonic LUMIX S 50mm F1.8'] },
        { category: 'レンズ（MFTマウント）', items: ['LEICA DG VARIO-ELMARIT 8-18mm/F2.8-4.0 ASPH./POWER O.I.S.', 'LEICA DG VARIO-ELMARIT 50-200mm/F2.8-4.0 ASPH./POWER O.I.S.', 'Panasonic LUMIX GX VARIO 12mm-35mm F2.8 POWER O.I.S', 'Panasonic LUMIX GX VARIO 35mm-100mm F2.8 POWER O.I.S'] },
        { category: 'オーディオミキサー', items: ['behringer Flow8'] }
      ]
    }
  ];

  const getColorClasses = (color: string) => {
    const colors: Record<string, { bg: string; text: string; light: string; gradient: string }> = {
      teal: { bg: 'bg-teal-500', text: 'text-teal-500', light: 'bg-teal-50', gradient: 'from-teal-500/10 to-transparent' },
      orange: { bg: 'bg-orange-500', text: 'text-orange-500', light: 'bg-orange-50', gradient: 'from-orange-500/10 to-transparent' },
      green: { bg: 'bg-green-500', text: 'text-green-500', light: 'bg-green-50', gradient: 'from-green-500/10 to-transparent' },
      pink: { bg: 'bg-pink-500', text: 'text-pink-500', light: 'bg-pink-50', gradient: 'from-pink-500/10 to-transparent' },
      violet: { bg: 'bg-violet-500', text: 'text-violet-500', light: 'bg-violet-50', gradient: 'from-violet-500/10 to-transparent' }
    };
    return colors[color];
  };

  return (
    <section id="services" ref={sectionRef} className="py-32 bg-white relative overflow-hidden">
      {/* くまチョップ（黒）左端から半身だけ見せる */}
      <div className="absolute left-0 bottom-1/3 z-0 w-44 h-44 opacity-40 pointer-events-none select-none hidden md:block" aria-hidden style={{ transform: 'translateX(-45%)' }}>
        <img src={`${import.meta.env.BASE_URL}kumachop-black.png`} alt="" className="w-full h-full object-contain object-center" />
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className={`mb-24 transform transition-all duration-1000 ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
        }`}>
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-1 bg-black"></div>
            <span className="text-xs font-bold tracking-[0.3em] uppercase text-gray-500">Our Services</span>
          </div>
          <h2 className="text-6xl md:text-7xl font-black text-black tracking-tight mb-4">
            SERVICES
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl">
            クリエイティブとテクノロジーを融合させた、包括的なデジタルソリューションを提供します
          </p>
        </div>

        {/* Services Grid（4枚） */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          {services.filter((s) => !s.fullWidth).map((service, index) => {
            const colors = getColorClasses(service.color);
            return (
              <div
                key={service.number}
                className={`group transform transition-all duration-1000 ${
                  isVisible ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'
                }`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                <div className="relative h-full bg-white border border-gray-100 hover:border-gray-200 transition-all duration-500 overflow-hidden">
                  <div className={`absolute inset-0 bg-gradient-to-br ${colors.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
                  <div className="relative p-10">
                    <div className="flex items-start justify-between mb-8">
                      <div className="flex items-center gap-4">
                        <div className={`w-16 h-16 ${colors.bg} flex items-center justify-center transition-transform duration-500 group-hover:scale-110`}>
                          <i className={`${service.icon} text-3xl text-white`}></i>
                        </div>
                        <span className={`text-7xl font-black ${colors.text} opacity-20`}>{service.number}</span>
                      </div>
                    </div>
                    <h3 className="text-2xl font-black text-black mb-2 group-hover:translate-x-2 transition-transform duration-300">{service.title}</h3>
                    <p className="text-xs font-medium tracking-wider uppercase text-gray-500 mb-6">{service.subtitle}</p>
                    <p className="text-sm text-gray-600 leading-relaxed mb-8">{service.description}</p>
                    <div className="space-y-3">
                      {service.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center gap-3 group/item">
                          <div className={`w-1.5 h-1.5 ${colors.bg} transition-all duration-300 group-hover/item:w-8`}></div>
                          <span className="text-sm font-medium text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                    {service.equipmentList && service.equipmentList.length > 0 && (
                      <div className="mt-6 pt-4 border-t border-gray-100">
                        <button
                          type="button"
                          onClick={() => setEquipmentModal({ title: service.title, list: service.equipmentList! })}
                          className={`text-sm font-medium ${colors.text} hover:underline cursor-pointer inline-flex items-center gap-1.5`}
                        >
                          <i className="ri-list-check-2"></i>機材リストを見る
                        </button>
                      </div>
                    )}
                    <div className={`absolute bottom-0 left-0 w-0 h-1 ${colors.bg} group-hover:w-full transition-all duration-700`}></div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* 映像オペレーション（コンテンツ内100%幅・画像付き） */}
        {(() => {
          const service = services.find((s) => s.fullWidth);
          if (!service) return null;
          const colors = getColorClasses(service.color);
          return (
            <div
              className={`w-full mb-20 transform transition-all duration-1000 ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'
              }`}
              style={{ transitionDelay: '600ms' }}
            >
              <div className="relative bg-white border border-gray-100 overflow-hidden">
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-0">
                  <div className="lg:col-span-2 relative aspect-[4/3] lg:aspect-auto lg:min-h-[280px] bg-gray-100 overflow-hidden">
                    {service.slideImages && service.slideImages.length > 0 ? (
                      service.slideImages.map((path, i) => (
                        <img
                          key={path}
                          src={`${baseUrl()}/${path}`}
                          alt={`${service.title} ${i + 1}`}
                          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
                            videoOpSlideIndex === i ? 'opacity-100 z-0' : 'opacity-0 z-0'
                          }`}
                        />
                      ))
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center text-gray-400">
                        <i className="ri-video-line text-6xl" />
                      </div>
                    )}
                  </div>
                  <div className="lg:col-span-3 relative p-8 md:p-12 flex flex-col justify-center">
                    <div className={`absolute inset-0 bg-gradient-to-br ${colors.gradient} opacity-30 pointer-events-none`} style={{ zIndex: 0 }}></div>
                    <div className="relative z-10">
                      <div className="flex items-center gap-4 mb-4">
                        <div className={`w-14 h-14 ${colors.bg} flex items-center justify-center`}>
                          <i className={`${service.icon} text-2xl text-white`}></i>
                        </div>
                        <span className={`text-6xl font-black ${colors.text} opacity-20`}>{service.number}</span>
                      </div>
                      <h3 className="text-2xl md:text-3xl font-black text-black mb-2">{service.title}</h3>
                      <p className="text-xs font-medium tracking-wider uppercase text-gray-500 mb-3">{service.subtitle}</p>
                      <p className="text-sm font-bold text-gray-900 mb-2">
                        学会運営（医療系を中心とした各種学会運営業務）を中核に、企画から当日運用まで一貫対応。
                      </p>
                      <p className="text-sm text-gray-600 leading-relaxed mb-6">{service.description}</p>
                      <div className="flex flex-wrap gap-2 mb-6">
                        {service.features.map((f, i) => (
                          <span key={i} className={`text-xs font-medium px-3 py-1 ${colors.light} ${colors.text} rounded-full ${i === 0 ? 'ring-2 ring-offset-1 ring-violet-400' : ''}`}>{f}</span>
                        ))}
                      </div>
                      <div className="flex flex-wrap items-center gap-4">
                        <Link
                          to="/services/event-video-operation"
                          className={`text-sm font-bold ${colors.text} hover:underline inline-flex items-center gap-1.5 border-b-2 border-current pb-0.5`}
                        >
                          詳細ページを見る
                          <i className="ri-arrow-right-line" />
                        </Link>
                        {service.equipmentList && service.equipmentList.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setEquipmentModal({ title: service.title, list: service.equipmentList! })}
                            className={`text-sm font-medium ${colors.text} hover:underline cursor-pointer inline-flex items-center gap-1.5`}
                          >
                            <i className="ri-list-check-2"></i>主な保有機材リストを見る
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className={`absolute bottom-0 left-0 w-full h-1 ${colors.bg}`}></div>
              </div>
            </div>
          );
        })()}

        {/* CTA Section */}
        <div className={`text-center transform transition-all duration-1000 delay-500 ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
        }`}>
          <div className="inline-block">
            <button 
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="group relative bg-black text-white px-12 py-5 font-bold text-base hover:bg-white hover:text-black border-2 border-black transition-all duration-300 whitespace-nowrap cursor-pointer overflow-hidden"
            >
              <span className="relative z-10">お見積もり・ご相談</span>
              <div className="absolute inset-0 bg-white transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300"></div>
            </button>
          </div>
        </div>
      </div>

      {/* 機材リストポップアップ */}
      {equipmentModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          onClick={() => setEquipmentModal(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="equipment-modal-title"
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-xl w-full max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <h3 id="equipment-modal-title" className="text-xl font-bold text-gray-900">
                {equipmentModal.title.includes('映像オペレーション') ? '主な保有機材' : `${equipmentModal.title} — 機材リスト`}
              </h3>
              <button
                type="button"
                onClick={() => setEquipmentModal(null)}
                className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600 cursor-pointer"
                aria-label="閉じる"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <ul className="space-y-6">
                {equipmentModal.list.map((cat, i) => (
                  <li key={i}>
                    <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-2">
                      {cat.category}
                    </h4>
                    <ul className="space-y-1">
                      {cat.items.map((item, j) => (
                        <li key={j} className="text-gray-800 flex items-center gap-2">
                          <span className="w-1.5 h-1.5 bg-violet-500 rounded-full flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}